<template>
  <div class="requirement-item-list">
    <p
      v-if="requirements.length == 0"
      class="message message-info mv-1 pl-2 requirement-item-list__norequirements"
    >
      {{ $t("texts.noActionRequirements") }}
    </p>
    <template v-for="(requirement, index) of requirements" :key="requirement.id">
      <div v-if="index > 0" class="requirement-item-list__separator">
        <strong>{{ $t("words.or") }}</strong>
      </div>
      <button-item
        :selected="
          variablesView.subSelection?.type == requirement.type &&
          variablesView.subSelection?.id == requirement.id
        "
        @click.prevent="select(requirement)"
        @contextmenu.prevent="openContextMenu(requirement, $event)"
      >
        <requirement-title :requirement="requirement" :index="index" />

        <template #icons>
          <button
            type="button"
            class="button-icon button-item__icon"
            @click.prevent="deleteRequirement(requirement)"
          >
            <lucide-icon name="trash-2" />
          </button>
        </template>
      </button-item>
    </template>
    <button class="button mt-2" type="button" @click.prevent="addRequirement">
      {{ $t("actions.addRequirement") }}
    </button>

    <context-menu
      :visible="contextMenuState.requirement != null"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="contextMenuState.requirement = null"
    >
      <button
        v-if="contextMenuState.requirement"
        type="button"
        class="context-menu__entry"
        @click="$emit('copy', contextMenuState.requirement)"
      >
        {{ $t("actions.copyRequirement") }}
      </button>

      <button
        type="button"
        class="context-menu__entry context-menu__entry--danger"
        @click="deleteRequirement(contextMenuState.requirement)"
      >
        <lucide-icon name="trash-2" class="align-middle" />
        {{ $t("actions.deleteRequirement") }}
      </button>
    </context-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { toUid } from "@/helpers/utility/Id";

import ButtonItem from "@core/input/ButtonItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import RequirementTitle from "./RequirementTitle.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";

import { useRequirementStore } from "@editor/items/requirement/requirements.store";
import { useVariablesViewStore } from "@editor/views/variables/variablesView.store";

import type { ConditionItem } from "../condition/ConditionItem";
import type { RequirementItem } from "./RequirementItem";

const props = defineProps<{
  itemType: string;
  itemId: number;
}>();

const emit = defineEmits<{
  (
    event: "select",
    item: ConditionItem | RequirementItem
  ): void;
  (
    event: "deselect",
    item: ConditionItem | RequirementItem
  ): void;
  (event: "copy", item: RequirementItem): void;
}>();

const variablesView = useVariablesViewStore();
const requirementStore = useRequirementStore();

const requirements = computed(() => {
  return requirementStore.byItemUid[toUid(props.itemType, props.itemId)] ?? [];
});

async function addRequirement() {
  const requirement = await requirementStore.create({
    itemId: props.itemId,
    itemType: props.itemType
  });
  emit("select", requirement);
}

function deleteRequirement(requirement: RequirementItem | null) {
  if (requirement == null) return;
  requirementStore.delete(requirement);
}

function select(item: ConditionItem | RequirementItem) {
  emit("select", item);
}

const contextMenuState = reactive({
  requirement: null as RequirementItem | null,
  x: 0,
  y: 0
});

function openContextMenu(requirement: RequirementItem, event: MouseEvent) {
  contextMenuState.requirement = requirement;
  contextMenuState.x = event.clientX;
  contextMenuState.y = event.clientY;
}
</script>

<style lang="scss">
.requirement-item-list {
  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(5rem, 7rem);
    gap: 0.25rem;
    align-items: center;
  }

  &__separator {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: center;

    // create a thin horizontal line,
    // separating the box vertically,
    // with a gap for the word "or" in the middle
    background-image: linear-gradient(
      to right,
      var(--color-text-light) 0%,
      var(--color-text-light) calc(50% - 3rem),
      transparent calc(50% - 3rem),
      transparent calc(50% + 3rem),
      var(--color-text-light) calc(50% + 3rem),
      var(--color-text-light) 100%
    );
    background-position: center;
    background-size: 100% 1px;
    background-repeat: no-repeat;
  }

  &__norequirements {
    color: var(--color-text-medium);
  }
}
</style>
